'use client';

import useWindowSize from '@/hooks/use-window';
import { Label } from '@radix-ui/react-label';
import { useMutation } from '@tanstack/react-query';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { X } from 'lucide-react';
import mixpanel from 'mixpanel-browser';

import { useEffect, useRef, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import GoogleBtn from '@/components/google-button';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Separator } from '@/components/ui/separator';

import { Tooltip } from '@/shared/components/tooltip';
import { APP_ENVS } from '@/shared/constants/envs';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { APP_ROUTES } from '@/shared/constants/navigation';
import { QUERY_PARAMS } from '@/shared/constants/query-params';
import { generateOTP, login } from '@/shared/services/auth';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { tabStorage } from '@/shared/utils/tab-storage';
import { normalizeEmail } from '@/shared/utils/validation';

import { useSilentRouter } from '@shared/hooks/use-router';

import { getApiErrorDetails } from '@/utils/axios';
import { shouldShowWebviewErrorModal } from '@/utils/common';
import { LOGIN_TOKEN_PARAM, REDIRECT_PATH } from '@/utils/constants';
import {
	ILoginEmailFormState,
	ILoginOTPFormState,
	loginEmailSchema,
	loginOTPSchema,
} from '@/utils/schema';

import { LogoAnimated } from '../common/logo-animated';
import AppLoader from '../ui/app-loader';
import { TLoginEmailSteps } from './auth-section.types';

const OTP_LENGTH = 6;

interface AuthSectionProps {
	onAuthSuccess?: () => void;
	skipRedirect?: boolean;
	insideOnboarding?: boolean;
	onClose?: () => void;
	showCloseButton?: boolean;
	closeButtonTooltip?: string;
}

function AuthSection({
	onAuthSuccess,
	skipRedirect = false,
	insideOnboarding = false,
	onClose,
	showCloseButton = false,
	closeButtonTooltip = 'Close',
}: AuthSectionProps = {}) {
	const otpInputRef = useRef<null | HTMLInputElement>(null);
	const emailInputRef = useRef<null | HTMLInputElement>(null);
	const [loginEmailStep, setLoginEmailStep] = useState<TLoginEmailSteps>('EMAIL');

	const router = useSilentRouter();
	const searchParams = useSearchParams();

	const { isDesktop } = useWindowSize();

	const canHideGoogleBtn = shouldShowWebviewErrorModal(isDesktop);
	const [hideGoogleBtn, setHideGoogleBtn] = useState<boolean>(false);
	const [emailFormState, setEmailFormState] = useState<ILoginEmailFormState>({
		email: '',
		validator: loginEmailSchema,
		error: '',
	});

	const [otpFormState, setOtpFormState] = useState<ILoginOTPFormState>({
		otp: '',
		validator: loginOTPSchema,
		error: '',
	});

	const googleAccessToken = decodeURIComponent(searchParams.get(LOGIN_TOKEN_PARAM) ?? '');
	const redirectPath = decodeURIComponent(searchParams.get(REDIRECT_PATH) ?? '');

	// Distinguishes a form-page signup from a normal signup. The form/applicant flow always
	// runs through the embedded modal (`insideOnboarding`), which re-mounts on the form page
	// after the Google OAuth round-trip too — so the prop alone is authoritative, no URL flag.
	const isFormPage = insideOnboarding;

	// useMutation for OTP generation
	const generateOTPMutation = useMutation({
		mutationFn: generateOTP,
		retry: undefined,
		onSuccess: () => {
			setLoginEmailStep('OTP');
			emitMixpanelEvent(mixpanelEvents.ACTION_LOGIN_OTP_SENT, {
				feature: MIXPANEL_APP_FEATURES.LOGIN_PAGE,
			});
		},
		onError: (error) => {
			const { errorMessage, errorStatusCode } = getApiErrorDetails(error);
			let displayError = errorMessage;

			if (errorStatusCode === 500) {
				const normalizedMessage = errorMessage.trimEnd().endsWith('.')
					? errorMessage.trimEnd()
					: `${errorMessage.trimEnd()}.`;
				displayError = `${normalizedMessage} Please re-check the email and try again.`;
			} else if (errorStatusCode === 422) {
				displayError = 'This email could not be found. Please re-check the email and try again.';
			}

			setEmailFormState((prev) => ({
				...prev,
				error: displayError,
			}));
		},
	});

	// useMutation for login
	const loginMutation = useMutation({
		mutationFn: login,
		onSuccess: (data, variables) => {
			tabStorage.clear();
			tabStorage.set('isFreshLogin', true);

			emitMixpanelEvent(
				variables.login_type === 'GOOGLE'
					? mixpanelEvents.COMPLETED_GOOGLE_LOGIN
					: mixpanelEvents.COMPLETED_LOGIN,
				{
					feature: MIXPANEL_APP_FEATURES.LOGIN_PAGE,
				}
			);

			if (data?.newUser && data?.email) {
				mixpanel.alias(data?.email);
				mixpanel.identify(data?.email);
			}

			// If onAuthSuccess callback provided (modal mode), use it instead of redirect
			if (onAuthSuccess) {
				onAuthSuccess();
				return;
			}

			// Skip redirect if skipRedirect is true
			if (skipRedirect) {
				return;
			}

			const params = new URLSearchParams(searchParams.toString());
			if (!!googleAccessToken) params.delete(LOGIN_TOKEN_PARAM);
			const query = params.toString();

			let path: string;
			if (data?.newUser && !redirectPath) {
				// New users without a redirectPath go to /ai; the `onboarding` marker tells the gate
				// this is a fresh signup (vs a returning user navigating to /ai directly).
				params.set(QUERY_PARAMS.ONBOARDING, 'true');
				path = `${APP_ROUTES.AI_AGENT}?${params.toString()}`;
			} else {
				path = redirectPath ? redirectPath : APP_ROUTES.ROOT;
				if (!!googleAccessToken && query) path += `?${query}`;
			}

			emitMixpanelEvent(mixpanelEvents.LOGIN_REDIRECTED, {
				feature: MIXPANEL_APP_FEATURES.LOGIN_PAGE,
				redirect_path: path,
				is_new_user: !!data?.newUser,
			});
			window.location.replace(path);
		},
		onError: (error) => {
			if (!!googleAccessToken) {
				const params = new URLSearchParams(searchParams.toString());
				params.delete(LOGIN_TOKEN_PARAM);
				router.replace(`?${params.toString()}`);
			}
			const { errorMessage, errorStatusCode } = getApiErrorDetails(error);
			if (loginEmailStep === 'EMAIL') {
				setEmailFormState((prev) => ({
					...prev,
					error: errorMessage,
				}));
			} else {
				if (errorStatusCode === 403) {
					setOtpFormState((prev) => ({
						...prev,
						error: 'Too many attempts!',
					}));
				} else {
					setOtpFormState((prev) => ({
						...prev,
						error: errorMessage,
					}));
				}
			}
		},
		retry: undefined,
	});

	const isLoading = generateOTPMutation.isPending || loginMutation.isPending || !!googleAccessToken;

	const cardHeader =
		loginEmailStep === 'EMAIL' ? `Welcome to ${APP_ENVS.COMPANY_NAME}` : 'Enter confirmation code';
	const cardDescription =
		loginEmailStep === 'EMAIL'
			? 'Please sign up or sign in below.'
			: `To confirm your email, we just sent a 6 digit code to ${emailFormState.email}.`;

	const handleCallGoogleLogin = (token: string | null) => {
		loginMutation.mutate({
			login_type: 'GOOGLE',
			token_id: token,
			otp: null,
			email: null,
			is_form_page: isFormPage,
		});
	};

	const handleGenerateOTP = (email: string) => {
		generateOTPMutation.mutate({
			email,
		});
	};

	const validateAndCallGenerateOTP = () => {
		const validationResult = loginEmailSchema.safeParse({
			email: emailFormState.email,
		});

		if (validationResult.error) {
			const fieldErrors = validationResult.error.flatten().fieldErrors;
			setEmailFormState((prev) => ({
				...prev,
				error: fieldErrors.email?.[0] ?? '',
			}));
			return;
		}

		handleGenerateOTP(validationResult.data.email);
	};

	const handleSubmitEmail = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (isLoading) return;

		validateAndCallGenerateOTP();
	};

	const handleOnChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (isLoading) return;

		const value = e.target.value;

		setEmailFormState((prev) => ({
			...prev,
			email: value,
			error: '',
		}));
	};

	const handleInputEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) return;
		if (e.key === 'Enter') {
			e.preventDefault();
			validateAndCallGenerateOTP();
		}
	};

	const handleSubmitOTPLogin = (e: React.FormEvent) => {
		e.preventDefault();
		if (isLoading) return;

		validateAndCallLogin(otpFormState.otp);
	};

	const handleOtpInputChange = (otp: string) => {
		if (isLoading) return;

		if (otp.length === OTP_LENGTH) {
			validateAndCallLogin(otp);
		}

		setOtpFormState((prevState) => ({
			...prevState,
			otp,
			error: '',
		}));
	};

	const handleOtpInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) return;

		if (e.key === 'Enter') {
			e.preventDefault();
			validateAndCallLogin(otpFormState.otp);
		}
	};

	const validateAndCallLogin = (otp: string) => {
		const validationResult = loginOTPSchema.safeParse({ otp });

		if (validationResult.error) {
			const fieldErrors = validationResult.error.flatten().fieldErrors;
			setOtpFormState((prevState) => ({
				...prevState,
				error: fieldErrors.otp?.[0] ?? '',
			}));
			return;
		}

		loginMutation.mutate({
			login_type: 'EMAIL',
			token_id: null,
			otp,
			email: normalizeEmail(emailFormState.email),
			is_form_page: isFormPage,
		});
	};

	useEffect(() => {
		if (loginEmailStep === 'OTP') otpInputRef?.current?.focus();
	}, [loginEmailStep]);

	useEffect(() => {
		if (loginEmailStep === 'EMAIL') {
			emailInputRef?.current?.focus();
		}
	}, [loginEmailStep]);

	useEffect(() => {
		setHideGoogleBtn(canHideGoogleBtn);
	}, [canHideGoogleBtn]);

	useEffect(() => {
		if (googleAccessToken) {
			handleCallGoogleLogin(googleAccessToken);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [googleAccessToken, isLoading]);

	useEffect(() => {
		if (insideOnboarding) {
			emitMixpanelEvent(mixpanelEvents.VIEWED_NEW_ONBOARDING_LOGIN_SCREEN, {
				feature: MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL,
			});
		}
	}, []);

	return (
		<div className="flex h-full w-full items-center justify-center">
			<Card className="relative w-full max-w-[400px]">
				{showCloseButton && onClose && (
					<Tooltip text={closeButtonTooltip}>
						<Button
							variant="ghost"
							size="icon"
							className="absolute right-2 top-2 z-50 h-8 w-8 rounded-full bg-white/90 text-gray-600 hover:bg-white hover:text-gray-900"
							onClick={onClose}
						>
							<X className="h-4 w-4" />
							<span className="sr-only">Close</span>
						</Button>
					</Tooltip>
				)}
				<CardHeader className="flex flex-col gap-3 px-4 sm:px-6">
					<LogoAnimated className="size-[50px]" />
					<div>
						<CardTitle className="text-2xl">{cardHeader}</CardTitle>
						<CardDescription className="mt-1 text-sm">{cardDescription}</CardDescription>
					</div>
				</CardHeader>
				<CardContent className="px-4 sm:px-6">
					{loginEmailStep === 'EMAIL' && (
						<>
							<form
								onSubmit={handleSubmitEmail}
								className="space-y-4"
							>
								<div className="space-y-2">
									{/* <Label htmlFor="email">Email</Label> */}
									<Input
										id="email"
										name="email"
										placeholder="you@email.com"
										ref={emailInputRef}
										value={emailFormState.email}
										onChange={handleOnChangeEmail}
										onKeyDown={handleInputEmailKeyDown}
									/>
									{emailFormState.error && (
										<p className="text-sm font-light text-red-500">{emailFormState.error}</p>
									)}
								</div>
								<Button
									type="submit"
									className="mt-4 h-auto w-full py-3"
									disabled={isLoading}
									onClick={() => {
										if (insideOnboarding) {
											emitMixpanelEvent(mixpanelEvents.CLICKED_ONBOARDING_SIGNIN_BUTTON, {
												feature: MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL,
											});
										}
									}}
								>
									Continue with email
									{isLoading && <AppLoader className="size-5" />}
								</Button>
							</form>
							{!hideGoogleBtn && (
								<div className="relative my-6">
									<Separator />
									<span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-sm text-gray-500">
										OR
									</span>
								</div>
							)}
							{!hideGoogleBtn && (
								<div className="w-full">
									<GoogleBtn insideOnboarding={insideOnboarding} />
								</div>
							)}
						</>
					)}
					{loginEmailStep === 'OTP' && (
						<form
							onSubmit={handleSubmitOTPLogin}
							className="space-y-6"
						>
							<div className="justify-center space-y-2">
								<Label
									htmlFor="otp-input-0"
									className="sr-only"
								>
									OTP Input
								</Label>
								<InputOTP
									ref={otpInputRef}
									maxLength={OTP_LENGTH}
									value={otpFormState.otp}
									onChange={handleOtpInputChange}
									pattern={REGEXP_ONLY_DIGITS}
									onKeyDown={handleOtpInputKeyDown}
									className="w-full"
								>
									<InputOTPGroup className="flex-1 justify-between">
										<InputOTPSlot index={0} />
										<InputOTPSlot index={1} />
										<InputOTPSlot index={2} />
										<InputOTPSlot index={3} />
										<InputOTPSlot index={4} />
										<InputOTPSlot index={5} />
									</InputOTPGroup>
								</InputOTP>
								{otpFormState.error && (
									<p className="text-start text-sm text-red-500">{otpFormState.error}</p>
								)}
							</div>
							<div className="mt-2 flex items-center">
								<Tooltip
									content={
										<p>
											If you haven&apos;t received the code, please check your spam folder once. If
											the issue persists, reach out to us at {APP_ENVS.COMPANY_SUPPORT_EMAIL}
										</p>
									}
								>
									<p className="text-sm">Didn&apos;t receive the code?</p>
								</Tooltip>
							</div>
							<Button
								type="submit"
								className="w-full"
								disabled={isLoading}
							>
								{isLoading ? 'Submitting...' : 'Submit'}
							</Button>
						</form>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

export default AuthSection;
